import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenText, CalendarDays, CheckCircle2, ChevronDown, ExternalLink, GraduationCap, LayoutDashboard, Link as LinkIcon, ListChecks, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  getProgressMetaKey,
  getProgressTasksKey,
  getVisibleProgressColleges,
} from '../helpers/progressSource';
import './HomePage.css';
import { userStorage } from '../lib/userStorage';

const HOME_LINKS_KEY = 'home_link_hub';
const HOME_SETUP_SKIPPED_KEY = 'home_setup_skipped';
const DEFAULT_HOME_LINKS = [
  { label: 'Common App', url: '' },
  { label: 'College Board', url: '' },
  { label: 'FAFSA', url: '' },
  { label: 'CSS Profile', url: '' },
  { label: 'Other Portal', url: '' },
];

function readJson(key, fallback) {
  try {
    const raw = userStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function parseLocalDate(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseLocalDate(value);
  if (!date) return 'No date set';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(value) {
  const date = parseLocalDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((date - today) / 86400000);
}

function normalizeHref(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function getDashboardCollegeCount(dashboards) {
  return dashboards.reduce((count, dashboard) => {
    const rows = readJson(`dashboard_${dashboard.id}_rows`, []);
    return count + (Array.isArray(rows) ? rows.length : 0);
  }, 0);
}

function getProgressItems() {
  const colleges = getVisibleProgressColleges();

  return colleges.map((college) => {
    const tasks = readJson(getProgressTasksKey(college.name), []);
    const meta = readJson(getProgressMetaKey(college.name), {});

    return {
      ...college,
      tasks: Array.isArray(tasks) ? tasks : [],
      meta: meta || {},
    };
  });
}

export function getNextDeadlines(items) {
  const deadlines = items
    .map((item) => ({
      college: item.name,
      date: item.meta.applyByDate || '',
      type: item.meta.applicationType || 'Application',
      submitted: Boolean(item.meta.applicationSubmitted),
    }))
    .filter((deadline) => deadline.date && !deadline.submitted)
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

  const nextDate = deadlines[0]?.date;
  return nextDate ? deadlines.filter((deadline) => deadline.date === nextDate) : [];
}

export function getNextTasks(items = [], generalCards = []) {
  const collegeTasks = items
    .filter((item) => !item.meta.applicationSubmitted)
    .flatMap((item) =>
    item.tasks
      .filter((task) => task.status !== 'complete')
      .map((task) => ({
        ...task,
        college: item.name,
      }))
  );

  const generalTasks = (Array.isArray(generalCards) ? generalCards : []).flatMap((card) =>
    (Array.isArray(card.items) ? card.items : [])
      .filter((item) => !item.complete)
      .map((item) => ({
        ...item,
        status: 'todo',
        college: `General Progress · ${card.title || 'Checklist'}`,
      }))
  );

  const tasks = [...collegeTasks, ...generalTasks];

  const datedTasks = tasks
    .filter((task) => task.date)
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));

  if (datedTasks.length) {
    const nextDate = datedTasks[0].date;
    return datedTasks.filter((task) => task.date === nextDate);
  }

  return tasks.slice(0, 4);
}

function getMyInfoStatus() {
  const academic = readJson('my_info_academic', {});
  const tests = readJson('my_info_testscores', {});
  const extracurriculars = readJson('my_info_extracurriculars', []);
  const awards = readJson('my_info_awards', []);

  const hasAcademic =
    Boolean(academic.gpa) ||
    Boolean(academic.intendedMajor) ||
    Boolean(tests.satGoal) ||
    Boolean(tests.actGoal);

  const hasApplicationPrep =
    (Array.isArray(extracurriculars) && extracurriculars.some((activity) => activity.organization || activity.position || activity.type)) ||
    (Array.isArray(awards) && awards.some((award) => award.title));

  return { hasAcademic, hasApplicationPrep };
}

function getFirstName(user) {
  const storedPersonal = readJson('my_info_personal', {});
  const rawName =
    storedPersonal.firstName ||
    user?.displayName ||
    user?.email?.split('@')[0] ||
    '';
  const cleanName = String(rawName).trim().split(/\s+/)[0];
  return cleanName && !cleanName.includes('@') ? cleanName : '';
}

function getChecklist({ dashboardCollegeCount, progressItems, nextDeadline, nextTask, hasAcademic, hasApplicationPrep }) {
  return [
    {
      label: 'Add colleges to a dashboard',
      detail: 'Use dashboards for research and comparison.',
      done: dashboardCollegeCount > 0,
      to: '/dashboard',
    },
    {
      label: 'Choose your final college list',
      detail: 'Progress should include the schools you are actively applying to.',
      done: progressItems.length > 0,
      to: '/progress',
    },
    {
      label: 'Set at least one apply-by date',
      detail: 'Deadlines power your Calendar and Up Next view.',
      done: Boolean(nextDeadline),
      to: '/progress',
    },
    {
      label: 'Add one unfinished task',
      detail: 'Tasks make the next step feel smaller.',
      done: Boolean(nextTask),
      to: '/progress',
    },
    {
      label: 'Fill in GPA, goals, or scores',
      detail: 'My Info keeps your academic snapshot ready.',
      done: hasAcademic,
      to: '/my-info',
    },
    {
      label: 'Start activities and awards',
      detail: 'Application Prep helps you collect Common App-style details early.',
      done: hasApplicationPrep,
      to: '/my-info',
    },
  ];
}

function HomePage({ dashboards }) {
  const { user } = useAuth();
  const [linkHub, setLinkHub] = useState(() => readJson(HOME_LINKS_KEY, DEFAULT_HOME_LINKS));
  const [setupSkipped, setSetupSkipped] = useState(() => readJson(HOME_SETUP_SKIPPED_KEY, false));
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const progressItems = useMemo(() => getProgressItems(), []);
  const generalProgressCards = useMemo(() => readJson('progress_general_cards', []), []);
  const activeProgressItems = useMemo(
    () => progressItems.filter((item) => !item.meta.applicationSubmitted),
    [progressItems]
  );
  const dashboardCollegeCount = useMemo(() => getDashboardCollegeCount(dashboards), [dashboards]);
  const nextDeadlines = useMemo(() => getNextDeadlines(activeProgressItems), [activeProgressItems]);
  const nextTasks = useMemo(
    () => getNextTasks(activeProgressItems, generalProgressCards),
    [activeProgressItems, generalProgressCards]
  );
  const nextDeadline = nextDeadlines[0] || null;
  const nextTask = nextTasks[0] || null;
  const { hasAcademic, hasApplicationPrep } = getMyInfoStatus();
  const checklist = getChecklist({
    dashboardCollegeCount,
    progressItems,
    nextDeadline,
    nextTask,
    hasAcademic,
    hasApplicationPrep,
  });
  const completedChecklist = checklist.filter((item) => item.done).length;
  const setupComplete = completedChecklist === checklist.length;
  const showingApplicationLinks = setupComplete || setupSkipped;
  const deadlineDistance = nextDeadline ? daysUntil(nextDeadline.date) : null;
  const firstName = getFirstName(user);

  const saveLinkHub = (nextLinks) => {
    setLinkHub(nextLinks);
    userStorage.setItem(HOME_LINKS_KEY, JSON.stringify(nextLinks));
  };

  const updateLink = (index, field, value) => {
    saveLinkHub(linkHub.map((link, linkIndex) => (
      linkIndex === index ? { ...link, [field]: value } : link
    )));
  };

  const addLink = () => {
    saveLinkHub([...linkHub, { label: 'New Link', url: '' }]);
  };

  const removeLink = (index) => {
    saveLinkHub(linkHub.filter((_, linkIndex) => linkIndex !== index));
  };

  const setSetupSkippedPreference = (skipped) => {
    setSetupSkipped(skipped);
    userStorage.setItem(HOME_SETUP_SKIPPED_KEY, JSON.stringify(skipped));
  };

  return (
    <div className="home-page">
      <header className="home-page__header">
        <div>
          <h1 className="home-page__title">Welcome back{firstName ? `, ${firstName}` : ''}</h1>
          <p className="home-page__subtitle">
            See your next deadlines, unfinished tasks, and application setup in one place.
          </p>
        </div>
      </header>

      <section className="home-page__up-next" aria-label="Up next">
        <article className="home-card">
          <div className="home-card__top">
            <div className="home-card__icon home-card__icon--deadline">
              <CalendarDays size={16} strokeWidth={1.8} />
            </div>
            <span className="home-card__label">Next Deadline</span>
          </div>
          <strong>{nextDeadlines.length ? formatDate(nextDeadline.date) : 'No deadline yet'}</strong>
          {nextDeadlines.length ? (
            <>
              <p>
                {deadlineDistance !== null ? (deadlineDistance < 0 ? 'Past due' : `${deadlineDistance} days away`) : 'Upcoming'}
              </p>
              <div className="home-card__list home-card__list--scroll">
                {nextDeadlines.map((deadline) => (
                  <span key={`${deadline.college}-${deadline.date}`}>
                    {deadline.college} · {deadline.type}
                  </span>
                ))}
              </div>
              {nextDeadlines.length > 2 && (
                <p className="home-card__scroll-hint">Scroll for more</p>
              )}
            </>
          ) : (
            <p>Set apply-by dates in Progress to see deadlines here.</p>
          )}
        </article>

        <article className="home-card">
          <div className="home-card__top">
            <div className="home-card__icon home-card__icon--task">
              <ListChecks size={16} strokeWidth={1.8} />
            </div>
            <span className="home-card__label">Next Unfinished Task</span>
          </div>
          <strong>{nextTasks.length && nextTask.date ? formatDate(nextTask.date) : nextTasks.length ? 'No date set' : 'No task yet'}</strong>
          {nextTasks.length ? (
            <>
              <div className="home-card__list home-card__list--scroll">
                {nextTasks.map((task) => (
                  <span key={`${task.college}-${task.id || task.text}-${task.date || 'no-date'}`}>
                    {task.text} · {task.college}
                  </span>
                ))}
              </div>
              {nextTasks.length > 2 && (
                <p className="home-card__scroll-hint">Scroll for more</p>
              )}
            </>
          ) : (
            <p>Add tasks under each college in Progress.</p>
          )}
        </article>

        <article className="home-card">
          <div className="home-card__top">
            <div className="home-card__icon home-card__icon--college">
              <GraduationCap size={16} strokeWidth={1.8} />
            </div>
            <span className="home-card__label">Final College List</span>
          </div>
          <strong>{activeProgressItems.length}</strong>
          <p>
            {activeProgressItems.length === 1
              ? '1 application is still active.'
              : `${activeProgressItems.length} applications are still active.`}
          </p>
        </article>
      </section>

      <div className="home-page__layout">
        <section className="home-section" aria-labelledby="checklist-title">
          <div className="home-section__header">
            <div>
              <h2 id="checklist-title">{showingApplicationLinks ? 'Application links' : 'Get MaiColleges ready'}</h2>
              {showingApplicationLinks && (
                <p className="home-section__subtitle">Save the websites you use often, like Common App, testing accounts, financial aid, and college portals.</p>
              )}
            </div>
            <div className="home-section__header-actions">
              {!setupComplete && !setupSkipped && <span className="home-section__count">{completedChecklist} / {checklist.length}</span>}
              {!setupComplete && (
                <button
                  className="home-section__text-action"
                  type="button"
                  onClick={() => setSetupSkippedPreference(!setupSkipped)}
                >
                  {setupSkipped ? 'View setup checklist' : 'Skip setup for now'}
                </button>
              )}
            </div>
          </div>

          {showingApplicationLinks ? (
            <div className="home-link-hub">
              {linkHub.map((link, index) => {
                const href = normalizeHref(link.url);
                return (
                  <div className="home-link-row" key={`${link.label}-${index}`}>
                    <div className="home-link-row__icon">
                      <LinkIcon size={16} strokeWidth={1.8} />
                    </div>
                    <input
                      className="home-link-row__name"
                      aria-label="Link name"
                      value={link.label}
                      onChange={(event) => updateLink(index, 'label', event.target.value)}
                    />
                    <input
                      className="home-link-row__url"
                      aria-label={`${link.label || 'Link'} URL`}
                      value={link.url}
                      onChange={(event) => updateLink(index, 'url', event.target.value)}
                      placeholder="https://..."
                    />
                    {href ? (
                      <a className="home-link-row__open" href={href} target="_blank" rel="noreferrer" aria-label={`Open ${link.label || 'link'}`}>
                        <ExternalLink size={15} strokeWidth={1.9} />
                      </a>
                    ) : (
                      <span className="home-link-row__open home-link-row__open--disabled" aria-hidden="true">
                        <ExternalLink size={15} strokeWidth={1.9} />
                      </span>
                    )}
                    {linkHub.length > 1 && (
                      <button className="home-link-row__remove" onClick={() => removeLink(index)} type="button" aria-label={`Remove ${link.label || 'link'}`}>
                        <Trash2 size={15} strokeWidth={1.8} />
                      </button>
                    )}
                  </div>
                );
              })}
              <button className="home-link-hub__add" onClick={addLink} type="button">
                <Plus size={15} strokeWidth={2} />
                Add Link
              </button>
            </div>
          ) : (
            <div className="home-checklist">
              {checklist.map((item) => (
                <Link className="home-checklist__item" to={item.to} key={item.label}>
                  <span className={`home-checklist__status ${item.done ? 'home-checklist__status--done' : ''}`}>
                    {item.done && <CheckCircle2 size={15} strokeWidth={2} />}
                  </span>
                  <span>
                    <strong>{item.label}</strong>
                    <small>{item.detail}</small>
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="home-section home-section--resources" aria-labelledby="resources-title">
          <button
            className="home-resources-toggle"
            type="button"
            aria-expanded={resourcesOpen}
            aria-controls="home-resources-list"
            onClick={() => setResourcesOpen((open) => !open)}
          >
            <span>
              <strong id="resources-title">Resources</strong>
              <small>Short guides for the parts of applications students tend to put off.</small>
            </span>
            <ChevronDown className={resourcesOpen ? 'home-resources-toggle__icon home-resources-toggle__icon--open' : 'home-resources-toggle__icon'} size={19} />
          </button>
          {resourcesOpen && <div className="home-resource-grid" id="home-resources-list">
            <Link to="/dashboard-template-guide" className="home-resource-card">
              <div className="home-resource-card__icon">
                <LayoutDashboard size={18} strokeWidth={1.8} />
              </div>
              <span>
                <strong>Dashboard Template Guide</strong>
                <small>Know what each dashboard is for and when it helps.</small>
              </span>
            </Link>
            <Link to="/application-timeline" className="home-resource-card">
              <div className="home-resource-card__icon">
                <BookOpenText size={18} strokeWidth={1.8} />
              </div>
              <span>
                <strong>Application Timeline</strong>
                <small>Know what to do junior year, senior summer, application season, and decision season.</small>
              </span>
            </Link>
            <Link to="/essay-game-plan" className="home-resource-card">
              <div className="home-resource-card__icon">
                <BookOpenText size={18} strokeWidth={1.8} />
              </div>
              <span>
                <strong>Essay Game Plan</strong>
                <small>Start early, write honestly, and make the essay about your growth.</small>
              </span>
            </Link>
            <Link to="/college-list-guide" className="home-resource-card">
              <div className="home-resource-card__icon">
                <BookOpenText size={18} strokeWidth={1.8} />
              </div>
              <span>
                <strong>Build Your College List</strong>
                <small>Balance fit, cost, deadlines, workload, and actual interest.</small>
              </span>
            </Link>
          </div>}
        </section>
      </div>
    </div>
  );
}

export default HomePage;
